"use client";

import { useEffect, useMemo, useRef, useState } from "react";

type ProfessionId = "infirmier" | "sage-femme" | "dentiste" | "kine" | "podologue";
type Verdict = "yes" | "conditional" | "renewal";

type Source = {
  label: string;
  publisher: "Légifrance" | "Ameli" | "Ordre";
  url: string;
};

type PrescriptionRule = {
  id: string;
  profession: ProfessionId;
  verdict: Verdict;
  title: string;
  answer: string;
  condition: string;
  keywords: string[];
  examples?: string[];
  sources: Source[];
  alert?: string;
};

const professions: Array<{
  id: ProfessionId;
  short: string;
  label: string;
  icon: string;
  scope: string;
}> = [
  { id: "infirmier", short: "IDE", label: "Infirmier", icon: "🩺", scope: "Produits de santé, plaies, vaccins, examens et renouvellements ciblés" },
  { id: "sage-femme", short: "SF", label: "Sage-femme", icon: "🤱", scope: "Femmes, nouveau-nés et situations précisément prévues par les textes" },
  { id: "dentiste", short: "CD", label: "Chirurgien-dentiste", icon: "🦷", scope: "Actes, produits et prestations nécessaires à l’art dentaire" },
  { id: "kine", short: "MK", label: "Masseur-kinésithérapeute", icon: "🦿", scope: "Liste limitative de dispositifs médicaux et renouvellements ciblés" },
  { id: "podologue", short: "PP", label: "Pédicure-podologue", icon: "🦶", scope: "Topiques, pansements et appareillages du pied dans son champ" },
];

const LEGIFRANCE_IDE_2026 = "https://www.legifrance.gouv.fr/loda/id/JORFTEXT000054321926";
const AMELI_IDE = "https://www.ameli.fr/pharmacien/exercice-professionnel/delivrance-produits-sante/droits-prescription/droit-prescription-infirmiers";
const ORDRE_IDE = "https://www.ordre-infirmiers.fr/publication-de-l-arrete-relatif-a-la-liste-des-actes-et-soins-infirmiers-et-de-l-arrete-relatif-a";
const ORDRE_SF = "https://www.ordre-sages-femmes.fr/exercice/vos-competences/le-droit-de-prescription/";
const LEGIFRANCE_SF = "https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000045301090";
const AMELI_SF = "https://www.ameli.fr/pharmacien/exercice-professionnel/delivrance-produits-sante/droits-prescription/droit-prescription-sages-femmes";
const LEGIFRANCE_DENTISTE = "https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000006688896";
const AMELI_KINE = "https://www.ameli.fr/pharmacien/exercice-professionnel/delivrance-produits-sante/droits-prescription/droit-prescription-masseurs-kinesitherapeutes";
const ORDRE_KINE = "https://alpesmaritimes.ordremk.fr/exercice-liberal/prescriptions-des-masseurs-kinesitherapeutes/";
const ORDRE_KINE_FAUTEUILS = "https://www.ordremk.fr/actualites/ordre/reforme-des-fauteuils-roulants-une-place-renforcee-des-kinesitherapeutes-dans-la-prescription/";
const AMELI_PODO = "https://www.ameli.fr/pharmacien/exercice-professionnel/delivrance-produits-sante/droits-prescription/droit-prescription-pedicures-podologues";
const ORDRE_PODO = "https://www.onpp.fr/exercice/la-profession/competences.html";

const rules: PrescriptionRule[] = [
  {
    id: "ide-plaies",
    profession: "infirmier",
    verdict: "yes",
    title: "Pansements et prise en charge de la plaie",
    answer: "Oui — prescription initiale possible, en principe limitée à 7 jours pour les pansements.",
    condition: "Dans le cadre de ses activités. La liste 2026 couvre notamment compresses, sets, hydrocolloïdes, hydrocellulaires, alginates, hydrogels, hydrofibres, interfaces, argent, super-absorbants, hémostatiques, champs stériles et rapprochement cutané.",
    keywords: ["pansement", "plaie", "compresse", "hydrocolloide", "hydrocellulaire", "alginate", "hydrogel", "hydrofibre", "argent", "interface", "vaseline", "set", "champ sterile", "sparadrap", "detersion", "hemostatique", "escarre"],
    examples: ["Pansement hydrocellulaire", "Compresses", "Set de plaie", "Pansement à l’argent"],
    sources: [
      { label: "Arrêté du 26 juin 2026 · art. 1 II", publisher: "Légifrance", url: LEGIFRANCE_IDE_2026 },
      { label: "Évolution de la prescription infirmière", publisher: "Ordre", url: ORDRE_IDE },
    ],
    alert: "Le mémo Ameli du 16 mars 2026 décrit encore l’ancien régime. Le texte Légifrance du 26 juin 2026 prévaut.",
  },
  {
    id: "ide-plaie-medicaments",
    profession: "infirmier",
    verdict: "conditional",
    title: "Produits associés au soin de plaie",
    answer: "Oui, dans les limites précises du texte.",
    condition: "Spray protecteur cutané ; anesthésique local non injectable ; antiseptique large spectre sans antibiotique pendant les 5 premiers jours d’une brûlure ou plaie traumatique souillée, hors pied diabétique ; nitrate d’argent faiblement dosé en cas d’hyperbourgeonnement.",
    keywords: ["antiseptique", "chlorhexidine", "anesthesique local", "lidocaine", "spray protecteur", "nitrate argent", "hyperbourgeonnement", "brulure", "plaie souillee"],
    examples: ["Antiseptique", "Anesthésique local", "Nitrate d’argent"],
    sources: [{ label: "Arrêté du 26 juin 2026 · art. 1 II", publisher: "Légifrance", url: LEGIFRANCE_IDE_2026 }],
  },
  {
    id: "ide-antalgique",
    profession: "infirmier",
    verdict: "yes",
    title: "Antalgiques de palier I",
    answer: "Oui — les IDE peuvent prescrire les antalgiques de palier I, y compris comme antipyrétiques.",
    condition: "Respecter le RCP, les contre-indications et le champ infirmier. Le texte permet aussi l’adaptation de posologie selon les indications de la prescription initiale dans la prise en charge de la douleur.",
    keywords: ["antalgique", "palier 1", "douleur", "fievre", "antipyretique", "paracetamol", "doliprane", "ibuprofene", "aspirine"],
    examples: ["Paracétamol", "Antipyrétique", "Douleur"],
    sources: [
      { label: "Arrêté du 26 juin 2026 · art. 1 V", publisher: "Légifrance", url: LEGIFRANCE_IDE_2026 },
      { label: "Présentation de la réforme", publisher: "Ordre", url: ORDRE_IDE },
    ],
  },
  {
    id: "ide-vaccins",
    profession: "infirmier",
    verdict: "conditional",
    title: "Vaccins",
    answer: "Oui, selon l’âge, le calendrier vaccinal et les conditions de déclaration/formation.",
    condition: "Vaccins du calendrier à partir de 11 ans, sauf vaccins vivants atténués chez l’immunodéprimé ; grippe à partir de 11 ans ; Covid-19 à partir de 5 ans. L’activité de prescription doit être déclarée à l’Ordre et peut exiger une formation.",
    keywords: ["vaccin", "vaccination", "grippe", "covid", "rappel", "vivant attenue", "immunodeprime"],
    examples: ["Vaccin grippe", "Vaccin Covid", "Rappel vaccinal"],
    sources: [{ label: "Arrêté du 26 juin 2026 · art. 1 I et annexe I", publisher: "Légifrance", url: LEGIFRANCE_IDE_2026 }],
  },
  {
    id: "ide-contraception",
    profession: "infirmier",
    verdict: "renewal",
    title: "Contraception et santé sexuelle",
    answer: "Renouvellement de pilule sous conditions ; prescription possible pour préservatifs et contraception d’urgence.",
    condition: "Pilule : ordonnance de moins d’un an, renouvellement maximal de 6 mois, non renouvelable. L’IDE peut aussi prescrire préservatifs, contraception d’urgence, bêta-HCG et certains dépistages d’IST.",
    keywords: ["pilule", "contraceptif", "contraception", "preservatif", "urgence", "lendemain", "beta hcg", "grossesse", "vih", "hepatite", "syphilis", "chlamydia", "gonocoque", "ist"],
    examples: ["Pilule", "Contraception d’urgence", "Préservatifs", "Dépistage IST"],
    sources: [{ label: "Arrêté du 26 juin 2026 · art. 1 III et annexe II", publisher: "Légifrance", url: LEGIFRANCE_IDE_2026 }],
  },
  {
    id: "ide-sevrage",
    profession: "infirmier",
    verdict: "yes",
    title: "Substituts nicotiniques",
    answer: "Oui — prescription des traitements nicotiniques de substitution.",
    condition: "L’IDE peut également prescrire un bilan cholestérol, triglycérides et glycémie à jeun pour l’évaluation du risque cardiovasculaire dans ce cadre.",
    keywords: ["tabac", "sevrage", "nicotine", "substitut", "patch", "gomme", "pastille", "cigarette"],
    examples: ["Patch nicotine", "Gommes nicotine"],
    sources: [
      { label: "Arrêté du 26 juin 2026 · art. 1 IV", publisher: "Légifrance", url: LEGIFRANCE_IDE_2026 },
      { label: "Droit de prescription IDE", publisher: "Ameli", url: AMELI_IDE },
    ],
  },
  {
    id: "ide-materiel",
    profession: "infirmier",
    verdict: "conditional",
    title: "Matériel, perfusion, incontinence et maintien à domicile",
    answer: "Oui, pour les catégories prévues par l’arrêté.",
    condition: "Cerceau de lit, location de béquilles/cannes, soulève-malade, incontinence/stomie, sondes, matériel de perfusion, sonde de nutrition entérale. Certains produits ne sont autorisés qu’en renouvellement à l’identique.",
    keywords: ["bequille", "canne", "souleve malade", "cerceau", "lit", "incontinence", "stomie", "poche", "sonde", "perfusion", "potence", "serum", "nutrition enterale", "catheter"],
    examples: ["Béquilles", "Sonde", "Matériel de perfusion", "Poche de stomie"],
    sources: [{ label: "Arrêté du 26 juin 2026 · art. 1 V", publisher: "Légifrance", url: LEGIFRANCE_IDE_2026 }],
  },
  {
    id: "ide-glycemie",
    profession: "infirmier",
    verdict: "renewal",
    title: "Autosurveillance glycémique",
    answer: "Renouvellement à l’identique.",
    condition: "Lancettes, bandelettes, autopiqueurs, aiguilles, accessoires et dispositifs de mesure du glucose interstitiel (capteurs et lecteurs). La prescription initiale doit être consultable par le pharmacien.",
    keywords: ["glycemie", "diabete", "bandelette", "lancette", "autopiqueur", "aiguille", "freestyle", "capteur", "lecteur", "glucose interstitiel"],
    examples: ["Bandelettes", "Lancettes", "Capteur glucose"],
    sources: [{ label: "Arrêté du 26 juin 2026 · art. 1 V et art. 3", publisher: "Légifrance", url: LEGIFRANCE_IDE_2026 }],
  },
  {
    id: "ide-biologie",
    profession: "infirmier",
    verdict: "conditional",
    title: "Examens biologiques",
    answer: "Oui, pour une liste précise et dans les situations définies.",
    condition: "INR (renouvellement), NFS/plaquettes/ionogramme, ECBU ± antibiogramme, glycémie, et chez un diabétique connu sous conditions : créatinine, rapport albuminurie/créatininurie et HbA1c.",
    keywords: ["prise de sang", "bilan", "inr", "avk", "nfs", "plaquette", "ionogramme", "ecbu", "antibiogramme", "glycemie", "creatinine", "albuminurie", "hba1c"],
    examples: ["ECBU", "NFS", "HbA1c", "INR"],
    sources: [{ label: "Arrêté du 26 juin 2026 · art. 1 VI", publisher: "Légifrance", url: LEGIFRANCE_IDE_2026 }],
  },
  {
    id: "sf-medicaments",
    profession: "sage-femme",
    verdict: "conditional",
    title: "Médicaments",
    answer: "Oui, uniquement pour les classes, patients, voies et situations prévues par l’annexe 41-4 du CSP.",
    condition: "La liste distingue femmes, nouveau-nés, hommes partenaires pour certaines IST, situations de renouvellement et urgence. Une classe autorisée par voie locale ne l’est pas automatiquement par voie orale ou injectable.",
    keywords: ["medicament", "antibiotique", "antifongique", "infection", "ist", "nouveau ne", "femme", "partenaire", "grossesse", "gynecologie"],
    examples: ["Médicament chez la femme", "Traitement IST", "Médicament du nouveau-né"],
    sources: [
      { label: "Annexe 41-4 du Code de la santé publique", publisher: "Légifrance", url: LEGIFRANCE_SF },
      { label: "Le droit de prescription", publisher: "Ordre", url: ORDRE_SF },
    ],
  },
  {
    id: "sf-dispositifs",
    profession: "sage-femme",
    verdict: "yes",
    title: "Dispositifs médicaux de suivi gynécologique et périnatal",
    answer: "Oui, pour la liste réglementaire.",
    condition: "Notamment ceinture de grossesse, contention des membres inférieurs, sonde/électrode périnéale, électrostimulateur périnéal, tire-lait, diaphragme, dispositif intra-utérin, matériel d’autosurveillance glycémique et pessaire.",
    keywords: ["tire lait", "ceinture grossesse", "contention", "bas", "sonde perineale", "electrode", "electrostimulateur", "diaphragme", "sterilet", "diu", "pessaire", "glycemie", "bandelette", "lancette"],
    examples: ["Tire-lait", "DIU", "Pessaire", "Lecteur de glycémie"],
    sources: [
      { label: "Annexe 41-4 du Code de la santé publique", publisher: "Légifrance", url: LEGIFRANCE_SF },
      { label: "Synthèse professionnelle", publisher: "Ordre", url: ORDRE_SF },
      { label: "Mémo pharmacien", publisher: "Ameli", url: AMELI_SF },
    ],
    alert: "La page Ameli consultée reprend encore l’ancienne liste de 2011. Pour les ajouts de 2022, consulter l’Ordre et Légifrance.",
  },
  {
    id: "sf-vaccins",
    profession: "sage-femme",
    verdict: "conditional",
    title: "Vaccins",
    answer: "Oui — ensemble des vaccins du calendrier pour les personnes ciblées, avec exceptions.",
    condition: "Exception des vaccins vivants atténués chez les personnes immunodéprimées. La grippe saisonnière peut être prescrite et administrée à toute la population.",
    keywords: ["vaccin", "vaccination", "grippe", "rappel", "immunodeprime", "vivant attenue"],
    examples: ["Vaccin grippe", "Vaccin du calendrier"],
    sources: [{ label: "Droit de prescription et vaccination", publisher: "Ordre", url: ORDRE_SF }],
  },
  {
    id: "sf-tabac",
    profession: "sage-femme",
    verdict: "yes",
    title: "Substituts nicotiniques",
    answer: "Oui — y compris pour l’entourage de la femme enceinte ou de l’enfant.",
    condition: "Rester dans le cadre et les catégories de patients définis par l’annexe 41-4.",
    keywords: ["tabac", "sevrage", "nicotine", "patch", "gomme", "pastille", "entourage"],
    examples: ["Patch nicotine", "Sevrage du partenaire"],
    sources: [{ label: "Annexe 41-4 et synthèse", publisher: "Ordre", url: ORDRE_SF }],
  },
  {
    id: "dentiste-champ",
    profession: "dentiste",
    verdict: "conditional",
    title: "Actes, produits et prestations",
    answer: "Oui, s’ils sont nécessaires à l’exercice de l’art dentaire.",
    condition: "Le droit n’est pas une autorisation générale hors champ bucco-dentaire. La cohérence entre le produit prescrit, l’indication et l’exercice de l’art dentaire doit être nette.",
    keywords: ["antibiotique", "antalgique", "bain de bouche", "dent", "dentaire", "buccal", "stomatologie", "parodontie", "produit", "acte", "radio", "radiographie"],
    examples: ["Antibiotique dentaire", "Antalgique", "Bain de bouche"],
    sources: [{ label: "Code de la santé publique · art. L4141-2", publisher: "Légifrance", url: LEGIFRANCE_DENTISTE }],
  },
  {
    id: "dentiste-tabac",
    profession: "dentiste",
    verdict: "yes",
    title: "Substituts nicotiniques",
    answer: "Oui — les chirurgiens-dentistes peuvent prescrire les traitements nicotiniques de substitution.",
    condition: "La délivrance reste soumise aux règles habituelles du produit prescrit et à sa prise en charge.",
    keywords: ["tabac", "sevrage", "nicotine", "patch", "gomme", "pastille"],
    examples: ["Patch nicotine", "Gommes nicotine"],
    sources: [{ label: "Synthèse droits de prescription", publisher: "Ameli", url: AMELI_IDE }],
  },
  {
    id: "kine-deambulation",
    profession: "kine",
    verdict: "yes",
    title: "Aides à la déambulation et au maintien",
    answer: "Oui, dans la liste limitative.",
    condition: "Cannes, béquilles, déambulateur, potence et soulève-malade, barrières et cerceaux de lit, embouts de canne.",
    keywords: ["canne", "bequille", "deambulateur", "potence", "souleve malade", "barriere lit", "cerceau", "embout"],
    examples: ["Béquilles", "Déambulateur", "Soulève-malade"],
    sources: [
      { label: "Droit de prescription MK", publisher: "Ameli", url: AMELI_KINE },
      { label: "Règles et liste pratique", publisher: "Ordre", url: ORDRE_KINE },
    ],
  },
  {
    id: "kine-ortheses",
    profession: "kine",
    verdict: "yes",
    title: "Orthèses, contention et aides de série",
    answer: "Oui, pour les dispositifs de série prévus par l’arrêté.",
    condition: "Attelles souples, ceintures lombaires et bandes-ceintures, contention souple élastique, talonnettes avec évidement et amortissantes.",
    keywords: ["attelle", "orthese", "contention", "bas", "chaussette", "ceinture lombaire", "bande ceinture", "talonnette"],
    examples: ["Attelle souple", "Ceinture lombaire", "Contention"],
    sources: [
      { label: "Droit de prescription MK", publisher: "Ameli", url: AMELI_KINE },
      { label: "Liste pratique", publisher: "Ordre", url: ORDRE_KINE },
    ],
  },
  {
    id: "kine-prevention",
    profession: "kine",
    verdict: "conditional",
    title: "Prévention des escarres, continence et respiration",
    answer: "Oui, pour les références limitativement prévues.",
    condition: "Matelas type gaufrier, certains coussins anti-escarres, sonde/électrode périnéale, collecteurs d’urines, étuis péniens, pessaires, urinal et débitmètre de pointe.",
    keywords: ["escarre", "matelas", "coussin", "sonde perineale", "electrode", "incontinence", "collecteur urine", "etui penien", "pessaire", "urinal", "debitmetre", "respiration"],
    examples: ["Matelas gaufrier", "Électrode périnéale", "Débitmètre de pointe"],
    sources: [{ label: "Droit de prescription MK", publisher: "Ameli", url: AMELI_KINE }],
  },
  {
    id: "kine-fauteuil",
    profession: "kine",
    verdict: "conditional",
    title: "Fauteuil roulant",
    answer: "Possible, mais la catégorie et le parcours de prescription doivent être vérifiés.",
    condition: "La réforme des VPH a modifié les parcours depuis le 1er décembre 2025. Les dispositifs complexes peuvent nécessiter un binôme avec un médecin qualifié en appareillage.",
    keywords: ["fauteuil roulant", "vph", "electrique", "manuel", "verticalisateur", "multiposition"],
    examples: ["Fauteuil manuel", "Fauteuil électrique"],
    sources: [
      { label: "Réforme VPH · point 2026", publisher: "Ordre", url: ORDRE_KINE_FAUTEUILS },
      { label: "Droit de prescription MK", publisher: "Ameli", url: AMELI_KINE },
    ],
    alert: "Ne pas appliquer automatiquement l’ancienne règle « classe I, location < 3 mois » sans contrôler la réforme VPH.",
  },
  {
    id: "kine-pansement",
    profession: "kine",
    verdict: "conditional",
    title: "Pansements de balnéothérapie",
    answer: "Oui, uniquement pour les pansements secs ou étanches nécessaires à l’immersion en balnéothérapie.",
    condition: "La liste générale des pansements de plaie n’est pas ouverte au kinésithérapeute par cette seule disposition.",
    keywords: ["pansement", "balneotherapie", "immersion", "etanche", "sec"],
    examples: ["Pansement étanche de balnéothérapie"],
    sources: [{ label: "Droit de prescription MK", publisher: "Ameli", url: AMELI_KINE }],
  },
  {
    id: "kine-tabac",
    profession: "kine",
    verdict: "yes",
    title: "Substituts nicotiniques",
    answer: "Oui — prescription des traitements nicotiniques de substitution.",
    condition: "Respecter les règles habituelles de prescription et de prise en charge.",
    keywords: ["tabac", "sevrage", "nicotine", "patch", "gomme", "pastille"],
    examples: ["Patch nicotine", "Gommes nicotine"],
    sources: [{ label: "Synthèse droits de prescription", publisher: "Ameli", url: AMELI_IDE }],
  },
  {
    id: "podo-topiques",
    profession: "podologue",
    verdict: "conditional",
    title: "Topiques à usage externe",
    answer: "Oui, dans le champ du pied et pour les catégories autorisées.",
    condition: "Antiseptiques, antifongiques, hémostatiques, anesthésiques, kératolytiques/verrucides, produits adoucissants/asséchants/calmants/cicatrisants/révulsifs, et anti-inflammatoires locaux pour hallux valgus ou ongle incarné. Substances vénéneuses exclues.",
    keywords: ["topique", "creme", "pommade", "antiseptique", "antifongique", "mycose", "hemostatique", "anesthesique", "keratolytique", "verrue", "verrucide", "hallux valgus", "ongle incarne", "anti inflammatoire"],
    examples: ["Antifongique local", "Verrucide", "Antiseptique"],
    sources: [
      { label: "Mémo pharmacien", publisher: "Ameli", url: AMELI_PODO },
      { label: "Compétences de la profession", publisher: "Ordre", url: ORDRE_PODO },
    ],
  },
  {
    id: "podo-pansements",
    profession: "podologue",
    verdict: "yes",
    title: "Pansements usuels du pied",
    answer: "Oui, pour la liste fixée par l’arrêté.",
    condition: "Compresses stériles coton/gaze/non tissées, sparadrap, dispositifs de maintien, sutures adhésives et compresses absorbantes.",
    keywords: ["pansement", "compresse", "gaze", "sparadrap", "jersey", "suture adhesive", "absorbante", "pied"],
    examples: ["Compresses", "Sparadrap", "Jersey tubulaire"],
    sources: [
      { label: "Mémo pharmacien", publisher: "Ameli", url: AMELI_PODO },
      { label: "Compétences de la profession", publisher: "Ordre", url: ORDRE_PODO },
    ],
  },
  {
    id: "podo-diabetique",
    profession: "podologue",
    verdict: "renewal",
    title: "Pansements pour patient diabétique",
    answer: "Renouvellement seulement — la prescription initiale est réservée au médecin.",
    condition: "Hydrocolloïdes, charbon actif, vaselinés, hydrofibres, hydrogels et alginate de calcium, dans le cadre prévu.",
    keywords: ["diabete", "pied diabetique", "hydrocolloide", "charbon", "vaseline", "hydrofibre", "hydrogel", "alginate", "pansement"],
    examples: ["Hydrocolloïde", "Hydrofibre", "Alginate"],
    sources: [{ label: "Mémo pharmacien", publisher: "Ameli", url: AMELI_PODO }],
  },
  {
    id: "podo-appareillage",
    profession: "podologue",
    verdict: "yes",
    title: "Orthèses et chaussures thérapeutiques",
    answer: "Oui, dans le champ de compétence podologique.",
    condition: "Prothèses et orthèses, onychoplasties, orthonyxies, orthoplasties, orthèses plantaires et chaussures thérapeutiques de série selon les règles de prise en charge applicables.",
    keywords: ["orthese plantaire", "semelle", "prothese", "onychoplastie", "orthonyxie", "orthoplastie", "chaussure therapeutique", "chaussure"],
    examples: ["Orthèse plantaire", "Chaussure thérapeutique"],
    sources: [{ label: "Compétences de la profession", publisher: "Ordre", url: ORDRE_PODO }],
  },
];

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr")
    .replace(/[’']/g, " ")
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const queryAliases: Record<string, string> = {
  ide: "infirmier",
  infirmiere: "infirmier",
  sf: "sage-femme",
  sagefemme: "sage-femme",
  kine: "kine",
  kinesitherapeute: "kine",
  mk: "kine",
  dentiste: "dentiste",
  chirurgien: "dentiste",
  pp: "podologue",
  podo: "podologue",
  pedicure: "podologue",
  doliprane: "paracetamol antalgique",
  sterilet: "diu dispositif intra uterin",
  freestyle: "capteur glucose interstitiel glycemie",
};

const expandQuery = (value: string) => {
  const normalized = normalize(value);
  const additions = Object.entries(queryAliases)
    .filter(([needle]) => normalized.includes(needle))
    .map(([, alias]) => alias);
  return normalize(`${normalized} ${additions.join(" ")}`);
};

const verdictMeta: Record<Verdict, { label: string; icon: string }> = {
  yes: { label: "Prescriptible", icon: "✓" },
  conditional: { label: "Sous conditions", icon: "!" },
  renewal: { label: "Renouvellement", icon: "↻" },
};

const sourceIcon = (publisher: Source["publisher"]) =>
  publisher === "Légifrance" ? "⚖️" : publisher === "Ameli" ? "🔵" : "🏛️";

const professionTerms = new Set([
  "ide", "infirmier", "infirmiere",
  "sf", "sage", "femme", "sage-femme",
  "cd", "chirurgien", "dentiste", "chirurgien-dentiste",
  "mk", "masseur", "kine", "kinesitherapeute", "masseur-kinesitherapeute",
  "pp", "pedicure", "podologue", "pedicure-podologue",
]);

const inferProfession = (value: string): ProfessionId | null => {
  const normalized = ` ${normalize(value)} `;
  if (/(^|\s)(ide|infirmier|infirmiere)(\s|$)/.test(normalized)) return "infirmier";
  if (/(^|\s)(sf|sage-femme)(\s|$)/.test(normalized)) return "sage-femme";
  if (/(^|\s)(cd|dentiste|chirurgien-dentiste)(\s|$)/.test(normalized)) return "dentiste";
  if (/(^|\s)(mk|kine|kinesitherapeute|masseur-kinesitherapeute)(\s|$)/.test(normalized)) return "kine";
  if (/(^|\s)(pp|podo|podologue|pedicure|pedicure-podologue)(\s|$)/.test(normalized)) return "podologue";
  return null;
};

export function PrescriptionRightsModule({ onBack }: { onBack: () => void }) {
  const [query, setQuery] = useState("");
  const [profession, setProfession] = useState<ProfessionId | "">("");
  const [activeRule, setActiveRule] = useState<string | null>(null);
  const [aiConfigured, setAiConfigured] = useState<boolean | null>(null);
  const [aiAnswer, setAiAnswer] = useState("");
  const [aiError, setAiError] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const expandedQuery = expandQuery(query);

  useEffect(() => {
    void fetch("/api/prescription-ai", { cache: "no-store" })
      .then((response) => response.ok ? response.json() : null)
      .then((payload) => setAiConfigured(Boolean(payload?.configured)))
      .catch(() => setAiConfigured(false));
  }, []);

  const results = useMemo(() => {
    if (!profession) return [];
    const selected = rules.filter((rule) => rule.profession === profession);
    if (!expandedQuery) return selected.slice(0, 8);

    const terms = expandedQuery.split(" ").filter((term) => term.length > 1);
    const subjectTerms = terms.filter((term) => !professionTerms.has(term));
    return selected
      .map((rule) => {
        const profile = professions.find((item) => item.id === rule.profession);
        const searchable = normalize([
          profile?.label,
          profile?.short,
          rule.title,
          rule.answer,
          rule.condition,
          ...rule.keywords,
          ...(rule.examples ?? []),
        ].join(" "));
        const subjectMatches = subjectTerms.filter((term) => searchable.includes(term));
        if (subjectTerms.length && !subjectMatches.length) return { rule, score: 0 };

        const score = terms.reduce((total, term) => {
          if (!searchable.includes(term)) return total;
          if (normalize(rule.title).includes(term)) return total + 7;
          if (rule.keywords.some((keyword) => normalize(keyword).includes(term))) return total + 5;
          if (normalize(profile?.label ?? "").includes(term) || normalize(profile?.short ?? "").includes(term)) return total + 6;
          return total + 2;
        }, 0);
        return { rule, score };
      })
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .map(({ rule }) => rule);
  }, [expandedQuery, profession, query]);

  const chooseProfession = (id: ProfessionId) => {
    setProfession(id);
    setActiveRule(null);
    setAiAnswer("");
    setAiError("");
    setHasSearched(false);
  };

  const runExample = (value: string) => {
    setQuery(value);
    setActiveRule(null);
    setAiAnswer("");
    setAiError("");
    setHasSearched(false);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const askAssistant = async () => {
    if (!query.trim()) {
      inputRef.current?.focus();
      setAiError("Saisissez d’abord la question à vérifier.");
      return;
    }
    if (!profession) {
      setAiError("Choisissez la profession du prescripteur avant de lancer l’analyse.");
      return;
    }
    setAiLoading(true);
    setAiAnswer("");
    setAiError("");
    setHasSearched(false);
    const contextRules = results.length
      ? results.slice(0, 6)
      : rules.filter((rule) => rule.profession === profession).slice(0, 8);
    const context = contextRules.map((rule) => {
      const profile = professions.find((item) => item.id === rule.profession);
      const sources = rule.sources.map((source) => `${source.publisher}: ${source.label} (${source.url})`).join(" ; ");
      return [
        `Profession : ${profile?.label ?? rule.profession}`,
        `Sujet : ${rule.title}`,
        `Réponse : ${rule.answer}`,
        `Condition : ${rule.condition}`,
        rule.alert ? `Alerte : ${rule.alert}` : "",
        `Sources : ${sources}`,
      ].filter(Boolean).join("\n");
    }).join("\n\n---\n\n");

    try {
      const response = await fetch("/api/prescription-ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: query, context }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      setAiAnswer(String(payload.answer ?? ""));
      setHasSearched(true);
      setActiveRule(results[0]?.id ?? null);
    } catch (error) {
      setAiError(error instanceof Error ? error.message : "L’assistant IA est momentanément indisponible.");
      setHasSearched(true);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <main className="prescription-rights-page">
      <header className="module-header prescription-rights-topbar">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="wordmark module-wordmark">
          <span className="wordmark-symbol"><i /><i /><i /></span>
          <span><strong>saint-laurent</strong><small>pharmacies</small></span>
        </div>
        <div className="module-header-actions">
          <a className="module-tools-shortcut" href="/?tools=1" title="Ouvrir les applications"><span>🧰</span><b>Applications</b></a>
          <span className="module-status"><i /> Vérifié le 27.07.2026</span>
        </div>
      </header>

      <section className="prescription-rights-hero">
        <div>
          <p className="prescription-eyebrow">⚖️ MÉMO COMPTOIR · SOURCES OFFICIELLES</p>
          <h1>Qui peut prescrire quoi&nbsp;?</h1>
          <p>Une réponse courte, la condition utile et le texte à ouvrir en cas de doute.</p>
        </div>
        <div className="prescription-source-stack" aria-label="Sources vérifiées">
          <span><b>3</b> sources croisées</span>
          <small>Ameli · Légifrance · Ordres</small>
        </div>
      </section>

      <section className="prescription-search-card">
        <div className="prescription-step-heading">
          <span>1</span>
          <div>
            <small>PRODUIT OU CLASSE</small>
            <label htmlFor="prescription-search">Que voulez-vous vérifier&nbsp;?</label>
          </div>
        </div>
        <div className="prescription-search-box">
          <span>⌕</span>
          <input
            ref={inputRef}
            id="prescription-search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveRule(null);
              setAiAnswer("");
              setAiError("");
              setHasSearched(false);
              if (!event.target.value.trim()) setProfession("");
            }}
            placeholder="Ex. Doliprane, pansements, tire-lait, antifongiques…"
            autoComplete="off"
          />
          {query && <button type="button" onClick={() => {
            setQuery("");
            setProfession("");
            setAiAnswer("");
            setAiError("");
            setHasSearched(false);
          }}>Effacer</button>}
        </div>
        <div className="prescription-quick-queries">
          <span>Essais rapides</span>
          {["Pansements", "Doliprane", "Tire-lait", "Fauteuil roulant", "Antifongiques"].map((item) => (
            <button key={item} type="button" onClick={() => runExample(item)}>{item}</button>
          ))}
        </div>
      </section>

      <section className={`prescription-profession-step ${query.trim() ? "" : "locked"}`}>
        <div className="prescription-step-heading">
          <span>2</span>
          <div>
            <small>PROFESSION</small>
            <h2>Qui a rédigé la prescription&nbsp;?</h2>
            <p>{query.trim() ? `Pour « ${query.trim()} », choisissez maintenant la profession.` : "Saisissez d’abord un produit ou une classe de produit."}</p>
          </div>
        </div>
        <nav className="prescription-profession-tabs" aria-label="Choisir la profession">
          {professions.map((item) => (
            <button key={item.id} type="button" disabled={!query.trim()} className={profession === item.id ? "active" : ""} onClick={() => chooseProfession(item.id)}>
              <span>{item.icon}</span><b>{item.label}</b><small>{rules.filter((rule) => rule.profession === item.id).length} repères</small>
            </button>
          ))}
        </nav>
      </section>

      <section className={`prescription-ai-card ${aiConfigured ? "ready" : ""} ${query.trim() && profession ? "unlocked" : "locked"}`}>
        <div className="prescription-ai-mark">✦</div>
        <div className="prescription-ai-copy">
          <small>ÉTAPE 3 · RECHERCHE AVEC IA</small>
          <h2>{profession ? `Analyser « ${query.trim()} » pour un ${professions.find((item) => item.id === profession)?.label.toLocaleLowerCase("fr")}` : "Choisissez le produit puis la profession"}</h2>
          <p>L’IA confronte votre demande au référentiel officiel, puis vous donne une réponse courte avec les conditions à contrôler.</p>
        </div>
        <span className="prescription-ai-status">{aiConfigured === null ? "Vérification…" : aiConfigured ? "● Connecté" : "○ Clé à configurer dans l’admin"}</span>
        <button type="button" disabled={!aiConfigured || aiLoading || !query.trim() || !profession} onClick={() => void askAssistant()}>
          {aiLoading ? "L’IA vérifie les textes…" : hasSearched ? "Relancer l’analyse IA" : "✦ Rechercher avec l’IA"}
        </button>
        {(aiAnswer || aiError) && (
          <div className={`prescription-ai-answer ${aiError ? "error" : ""}`}>
            <strong>{aiError ? "Recherche à compléter" : "Réponse IA"}</strong>
            <p>{aiError || aiAnswer}</p>
          </div>
        )}
      </section>

      {hasSearched && <section className="prescription-results-layout">
        <div className="prescription-results">
          <div className="prescription-results-heading">
            <div>
              <p>REPÈRES OFFICIELS UTILISÉS PAR L’IA</p>
              <h2>{results.length ? `${results.length} correspondance${results.length > 1 ? "s" : ""}` : "Aucune correspondance exacte"}</h2>
            </div>
            {query && <span>Recherche élargie aux synonymes</span>}
          </div>

          {!results.length && (
            <div className="prescription-no-result">
              <span>?</span>
              <div>
                <h3>Ne pas conclure à une interdiction.</h3>
                <p>L’IA a recherché dans le référentiel de la profession choisie, mais aucun repère exact n’a été trouvé. Reformulez avec la classe du produit ou ouvrez les textes officiels ci-contre.</p>
              </div>
            </div>
          )}

          {results.map((rule) => {
            const profile = professions.find((item) => item.id === rule.profession)!;
            const isOpen = activeRule === rule.id;
            const verdict = verdictMeta[rule.verdict];
            return (
              <article key={rule.id} className={`prescription-result-card ${rule.verdict} ${isOpen ? "open" : ""}`}>
                <button type="button" className="prescription-result-summary" onClick={() => setActiveRule(isOpen ? null : rule.id)} aria-expanded={isOpen}>
                  <span className="prescription-profession-mark">{profile.icon}</span>
                  <span className="prescription-result-copy">
                    <small>{profile.label}</small>
                    <strong>{rule.title}</strong>
                    <em>{rule.answer}</em>
                  </span>
                  <span className={`prescription-verdict ${rule.verdict}`}><b>{verdict.icon}</b>{verdict.label}</span>
                  <span className="prescription-chevron">{isOpen ? "−" : "+"}</span>
                </button>

                {isOpen && (
                  <div className="prescription-result-detail">
                    <div className="prescription-condition">
                      <span>À contrôler au comptoir</span>
                      <p>{rule.condition}</p>
                    </div>
                    {rule.alert && <p className="prescription-alert">⚠️ {rule.alert}</p>}
                    {!!rule.examples?.length && (
                      <div className="prescription-examples">
                        <span>Exemples couverts</span>
                        <div>{rule.examples.map((example) => <i key={example}>{example}</i>)}</div>
                      </div>
                    )}
                    <div className="prescription-rule-sources">
                      {rule.sources.map((source) => (
                        <a key={`${rule.id}-${source.label}`} href={source.url} target="_blank" rel="noreferrer">
                          <span>{sourceIcon(source.publisher)}</span>
                          <b>{source.publisher}</b>
                          <small>{source.label}</small>
                          <em>↗</em>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <aside className="prescription-reference-panel">
          <div className="prescription-reference-intro">
            <span>🛡️</span>
            <div>
              <b>Réponse sécurisée</b>
              <p>L’IA n’invente pas le droit. La recherche interroge uniquement ce référentiel sourcé.</p>
            </div>
          </div>
          <h3>Accès direct aux références</h3>
          <a href={LEGIFRANCE_IDE_2026} target="_blank" rel="noreferrer"><span>⚖️</span><div><b>Réforme IDE 2026</b><small>Texte en vigueur depuis le 28 juin</small></div><em>↗</em></a>
          <a href={ORDRE_SF} target="_blank" rel="noreferrer"><span>🤱</span><div><b>Sages-femmes</b><small>Listes, patients et voies autorisées</small></div><em>↗</em></a>
          <a href={LEGIFRANCE_DENTISTE} target="_blank" rel="noreferrer"><span>🦷</span><div><b>Chirurgiens-dentistes</b><small>Article L4141-2 du CSP</small></div><em>↗</em></a>
          <a href={AMELI_KINE} target="_blank" rel="noreferrer"><span>🦿</span><div><b>Kinésithérapeutes</b><small>Liste limitative des dispositifs</small></div><em>↗</em></a>
          <a href={AMELI_PODO} target="_blank" rel="noreferrer"><span>🦶</span><div><b>Pédicures-podologues</b><small>Topiques et pansements</small></div><em>↗</em></a>
          <div className="prescription-reference-warning">
            <b>Ce mémo oriente, il ne valide pas l’ordonnance.</b>
            <p>Contrôler aussi l’identité, le RPPS, la date, la signature, la cohérence clinique, le RCP et les conditions de remboursement.</p>
          </div>
        </aside>
      </section>}
    </main>
  );
}
